import { toast } from 'react-toastify'

export interface CameraCaptureOptions {
  currentStep: string
  onFileCapture: (file: File, documentType?: string) => void
  addressFiles: File[]
  identityFiles: File[]
  relationshipFiles: File[]
}

export const useCameraCapture = () => {
  const showDocumentTypeSelector = (file: File, closeCamera: () => void, options: CameraCaptureOptions) => {
    closeCamera()

    const selectorModal = document.createElement('div')
    selectorModal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.8);
      z-index: 10001;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 20px;
      box-sizing: border-box;
    `

    const container = document.createElement('div')
    container.style.cssText = `
      background: white;
      border-radius: 12px;
      padding: 30px;
      max-width: 400px;
      width: 90%;
      text-align: center;
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
    `

    const title = document.createElement('h3')
    title.textContent = 'Selecione o tipo de documento'
    title.style.cssText = `
      margin: 0 0 20px 0;
      color: #2D3748;
      font-size: 20px;
      font-weight: 600;
    `

    const buttonsContainer = document.createElement('div')
    buttonsContainer.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: 12px;
    `

    const createButton = (text: string, onClick: () => void, disabled = false) => {
      const button = document.createElement('button')
      button.textContent = text
      button.style.cssText = `
        background: ${disabled ? '#E2E8F0' : '#6B7B3C'};
        color: ${disabled ? '#A0AEC0' : 'white'};
        border: none;
        padding: 14px 20px;
        border-radius: 8px;
        font-size: 16px;
        cursor: ${disabled ? 'not-allowed' : 'pointer'};
        font-weight: 500;
        transition: background-color 0.2s;
      `
      
      if (!disabled) {
        button.addEventListener('mouseenter', () => {
          button.style.background = '#5A6B32'
        })
        button.addEventListener('mouseleave', () => {
          button.style.background = '#6B7B3C'
        })
        button.addEventListener('click', onClick)
      }
      
      return button
    }

    const addressButton = createButton(
      `Comprovante de Endereço ${options.addressFiles.length > 0 ? '(já enviado)' : ''}`,
      () => {
        options.onFileCapture(file, 'address')
        toast.success('Foto do comprovante de endereço capturada!')
        closeSelectorModal()
      },
      options.addressFiles.length > 0
    )

    const identityButton = createButton(
      `Documento de Identidade ${options.identityFiles.length > 0 ? '(já enviado)' : ''}`,
      () => {
        options.onFileCapture(file, 'identity')
        toast.success('Foto do documento de identidade capturada!')
        closeSelectorModal()
      },
      options.identityFiles.length > 0
    )

    const relationshipButton = createButton(
      `Documento de Vínculo ${options.relationshipFiles.length > 0 ? '(já enviado)' : ''}`,
      () => {
        options.onFileCapture(file, 'relationship')
        toast.success('Foto do documento de vínculo capturada!')
        closeSelectorModal()
      },
      options.relationshipFiles.length > 0
    )

    const cancelButton = createButton(
      'Cancelar',
      () => closeSelectorModal()
    )
    cancelButton.style.background = '#E53E3E'
    cancelButton.addEventListener('mouseenter', () => {
      cancelButton.style.background = '#C53030'
    })
    cancelButton.addEventListener('mouseleave', () => {
      cancelButton.style.background = '#E53E3E'
    })

    const closeSelectorModal = () => {
      document.body.removeChild(selectorModal)
    }

    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeSelectorModal()
        document.removeEventListener('keydown', handleKeyPress)
      }
    }
    document.addEventListener('keydown', handleKeyPress)

    buttonsContainer.appendChild(addressButton)
    buttonsContainer.appendChild(identityButton)
    buttonsContainer.appendChild(relationshipButton)
    buttonsContainer.appendChild(cancelButton)
    
    container.appendChild(title)
    container.appendChild(buttonsContainer)
    selectorModal.appendChild(container)
    document.body.appendChild(selectorModal)
  }

  const capturePhoto = async (options: CameraCaptureOptions) => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast.error('Seu navegador não suporta acesso à câmera')
        return
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      })
      
      const video = document.createElement('video')
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')

      if (!context) {
        stream.getTracks().forEach(track => track.stop())
        toast.error('Erro ao inicializar a câmera')
        return
      }

      video.srcObject = stream
      video.autoplay = true
      video.playsInline = true

      const modal = document.createElement('div')
      modal.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.9);
        z-index: 10000;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: space-between;
        padding: 20px;
        box-sizing: border-box;
      `

      const instructions = document.createElement('div')
      instructions.style.cssText = `
        color: white;
        text-align: center;
        font-size: 16px;
        font-weight: 500;
        flex-shrink: 0;
        margin-bottom: 10px;
      `
      
      const getInstructionText = () => {
        switch (options.currentStep) {
          case 'prescription':
            return 'Posicione a receita médica de forma clara e legível'
          case 'pill_prescription':
            return 'Posicione a receita do medicamento controlado de forma clara e legível'
          case 'anvisa':
            return 'Posicione a autorização ANVISA de forma clara'
          case 'laudo_medico':
            return 'Posicione o laudo médico de forma clara'
          default:
            return 'Posicione o documento de forma clara e legível'
        }
      }
      
      instructions.textContent = getInstructionText()


      const videoContainer = document.createElement('div')
      videoContainer.style.cssText = `
        position: relative;
        width: 85%;
        height: 70vh;
        border-radius: 12px;
        overflow: hidden;
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
      `

      video.style.cssText = `
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 12px;
        background: black;
        display: block;
      `

      const overlay = document.createElement('div')
      overlay.style.cssText = `
        position: absolute;
        top: 1%;
        left: 2%;
        right: 2%;
        bottom: 1%;
        border: 1px solid #6B7B3C;
        border-radius: 12px;
        pointer-events: none;
        box-shadow: 
          inset 0 0 0 2px rgba(107, 123, 60, 0.3),
          0 0 0 9999px rgba(0, 0, 0, 0.4);
      `

    //   const corners = ['top-left', 'top-right', 'bottom-left', 'bottom-right']
    //   corners.forEach(corner => {
    //     const cornerElement = document.createElement('div')
    //     const isTop = corner.includes('top')
    //     const isLeft = corner.includes('left')
        
    //     cornerElement.style.cssText = `
    //       position: absolute;
    //       width: 20px;
    //       height: 20px;
    //       border: 1px solid #6B7B3C;
    //       ${isTop ? 'top: -2px' : 'bottom: -2px'};
    //       ${isLeft ? 'left: -2px' : 'right: -2px'};
    //       ${isTop ? 'border-bottom: none' : 'border-top: none'};
    //       ${isLeft ? 'border-right: none' : 'border-left: none'};
    //     `
    //     overlay.appendChild(cornerElement)
    //   })

      videoContainer.appendChild(video)
      videoContainer.appendChild(overlay)

      const buttonsContainer = document.createElement('div')
      buttonsContainer.style.cssText = `
        display: flex;
        gap: 15px;
        margin-top: 15px;
        flex-wrap: wrap;
        justify-content: center;
        flex-shrink: 0;
      `

      const captureButton = document.createElement('button')
      captureButton.textContent = 'Capturar'
      captureButton.style.cssText = `
        background: #6B7B3C;
        color: white;
        border: none;
        padding: 16px 32px;
        border-radius: 50px;
        font-size: 18px;
        cursor: pointer;
        font-weight: 600;
        box-shadow: 0 4px 12px rgba(107, 123, 60, 0.3);
        transition: all 0.2s;
      `

      const switchButton = document.createElement('button')
      switchButton.textContent = 'Trocar'
      switchButton.style.cssText = `
        background: #4A5568;
        color: white;
        border: none;
        padding: 12px 20px;
        border-radius: 25px;
        font-size: 14px;
        cursor: pointer;
        font-weight: 500;
      `

      const cancelButton = document.createElement('button')
      cancelButton.textContent = 'Fechar'
      cancelButton.style.cssText = `
        background: #E53E3E;
        color: white;
        border: none;
        padding: 12px 20px;
        border-radius: 25px;
        font-size: 14px;
        cursor: pointer;
        font-weight: 500;
      `

      let currentFacingMode = 'environment'

      const switchCamera = async () => {
        try {
          stream.getTracks().forEach(track => track.stop())
          currentFacingMode = currentFacingMode === 'environment' ? 'user' : 'environment'
          
          const newStream = await navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: currentFacingMode,
              width: { ideal: 1920 },
              height: { ideal: 1080 }
            }
          })
          
          video.srcObject = newStream
        } catch (error) {
          console.error('Erro ao trocar câmera:', error)
          toast.error('Erro ao trocar câmera')
        }
      }

      const capturePhotoAction = () => {
        const flash = document.createElement('div')
        flash.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: white;
          z-index: 10002;
          opacity: 0.8;
          pointer-events: none;
        `
        document.body.appendChild(flash)
        
        setTimeout(() => {
          document.body.removeChild(flash)
        }, 200)

        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        context.drawImage(video, 0, 0)

        canvas.toBlob((blob) => {
          if (blob) {
            const timestamp = new Date().getTime()
            const file = new File([blob], `camera-photo-${timestamp}.jpg`, {
              type: 'image/jpeg',
              lastModified: timestamp
            })

            switch (options.currentStep) {
              case 'prescription':
                options.onFileCapture(file, 'prescription')
                toast.success('Foto da receita capturada com sucesso!')
                break
              case 'pill_prescription':
                options.onFileCapture(file, 'pill_prescription')
                toast.success('Foto da receita do medicamento controlado capturada!')
                break
              case 'otherDocuments':
                showDocumentTypeSelector(file, closeModal, options)
                return
              case 'anvisa':
                options.onFileCapture(file, 'anvisa')
                toast.success('Foto da autorização ANVISA capturada!')
                break
              default:
                options.onFileCapture(file, 'prescription')
                toast.success('Foto capturada com sucesso!')
            }

            closeModal()
          }
        }, 'image/jpeg', 0.9)
      }

      const closeModal = () => {
        stream.getTracks().forEach(track => track.stop())
        document.body.removeChild(modal)
      }

      captureButton.addEventListener('click', capturePhotoAction)
      switchButton.addEventListener('click', switchCamera)
      cancelButton.addEventListener('click', closeModal)

      const handleKeyPress = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          closeModal()
          document.removeEventListener('keydown', handleKeyPress)
        }
      }
      document.addEventListener('keydown', handleKeyPress)

      buttonsContainer.appendChild(captureButton)
      buttonsContainer.appendChild(switchButton)
      buttonsContainer.appendChild(cancelButton)
      
      modal.appendChild(instructions)
      modal.appendChild(videoContainer)
      modal.appendChild(buttonsContainer)
      document.body.appendChild(modal)

      video.addEventListener('loadedmetadata', async () => {
        try {
          const devices = await navigator.mediaDevices.enumerateDevices()
          const videoDevices = devices.filter(device => device.kind === 'videoinput')

          if (videoDevices.length <= 1) {
            switchButton.style.display = 'none'
          }
        } catch (error) {
          console.error('Erro ao enumerar dispositivos:', error)
          switchButton.style.display = 'none'
        }
      })

    } catch (error) {
      console.error('Erro ao acessar câmera:', error)
      
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError') {
          toast.error('Permissão de câmera negada. Permita o acesso à câmera nas configurações do navegador.')
        } else if (error.name === 'NotFoundError') {
          toast.error('Nenhuma câmera foi encontrada neste dispositivo.')
        } else if (error.name === 'NotReadableError') {
          toast.error('Câmera está sendo usada por outro aplicativo.')
        } else {
          toast.error('Erro ao acessar a câmera. Tente novamente.')
        }
      } else {
        toast.error('Erro desconhecido ao acessar a câmera.')
      }
    }
  }

  return { capturePhoto }
}
